import React, { useState } from 'react';
import type { Decision } from '../../../lib/aiJobSummary';
import { Badge, Icon } from '../../ui';

/**
 * WHAT YOU NEED TO KNOW: the one loud section on the page. Big heading,
 * a 3px amber rule down the left, the caveats, and inside it the decisions
 * the operator has to make. Answering here is the real thing: it writes to the
 * job, and the Approve to Build dialog then only stops for what is still
 * unanswered.
 */
export default function NeedToKnow({
  items,
  decisions,
  readOnly = false,
  onAnswer,
}: {
  items: string[];
  decisions: Decision[];
  readOnly?: boolean;
  /** Records the answer on the job. Absent on read-only renders. */
  onAnswer?: (decisionId: string, answer: string | null) => Promise<void>;
}) {
  if (items.length === 0 && decisions.length === 0) return null;
  return (
    <section
      style={{
        position: 'relative',
        padding: '18px 20px 18px 24px',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--surface-card)',
        border: '1px solid var(--border-hairline)',
        boxShadow: 'var(--shadow-rest)',
        overflow: 'hidden',
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '3px',
          background: 'var(--status-warn)',
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--status-warn-soft)',
            color: 'var(--status-warn)',
            flexShrink: 0,
          }}
        >
          <Icon name="triangle-alert" size={15} strokeWidth={2} />
        </span>
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--text-xl)',
            fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
            letterSpacing: 'var(--tracking-tight)',
            lineHeight: 'var(--leading-tight)',
            color: 'var(--text-body)',
          }}
        >
          What you need to know
        </h2>
      </div>

      {items.length > 0 ? (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {items.map((item, i) => (
            <li
              key={i}
              style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start',
                fontSize: 'var(--text-base)',
                lineHeight: 'var(--leading-normal)',
                color: 'var(--text-body)',
              }}
            >
              <span
                aria-hidden
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: 'var(--radius-pill)',
                  background: 'var(--status-warn)',
                  marginTop: '8px',
                  flexShrink: 0,
                }}
              />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {decisions.length > 0 ? (
        <div
          style={{
            marginTop: items.length > 0 ? '16px' : 0,
            padding: '14px 16px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--accent-soft)',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
          }}
        >
          <div className="t-eyebrow" style={{ color: 'var(--accent-text)' }}>
            {decisions.length === 1 ? 'Decision' : `Decisions · ${decisions.length}`}
          </div>
          {decisions.map((d) => (
            <DecisionRow key={d.id} decision={d} readOnly={readOnly || !onAnswer} onAnswer={onAnswer} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function DecisionRow({
  decision,
  readOnly,
  onAnswer,
}: {
  decision: Decision;
  readOnly: boolean;
  onAnswer?: (decisionId: string, answer: string | null) => Promise<void>;
}) {
  const options = decision.options ?? [];
  const answer = decision.answer?.trim() || null;
  const isCustom = answer !== null && !options.includes(answer);
  const [draft, setDraft] = useState(isCustom ? answer : '');
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const save = async (value: string | null) => {
    if (!onAnswer || readOnly || saving) return;
    setSaving(true);
    setFailed(false);
    try {
      await onAnswer(decision.id, value);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  const commitDraft = () => {
    const next = draft.trim();
    if (next === (isCustom ? answer : '')) return;
    void save(next || null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
        <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--accent-text)', paddingTop: '3px', flexShrink: 0 }}>
          {decision.id}
        </span>
        <span style={{ fontSize: 'var(--text-base)', lineHeight: 'var(--leading-normal)', color: 'var(--text-body)' }}>
          {decision.question}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', paddingLeft: '30px' }}>
        {options.map((opt) => {
          const selected = answer === opt;
          const recommended = decision.recommendation === opt;
          return (
            <button
              key={opt}
              type="button"
              className={`ck-op-option pressable${selected ? ' is-selected' : ''}`}
              disabled={readOnly || saving}
              aria-pressed={selected}
              title={selected ? 'Click again to un-answer' : `Answer ${decision.id} with "${opt}"`}
              onClick={() => void save(selected ? null : opt)}
            >
              {selected ? <Icon name="check" size={13} strokeWidth={2.25} /> : null}
              {opt}
              {recommended && !selected ? (
                <span className="t-eyebrow" style={{ fontSize: '9px', color: 'var(--accent-text)', letterSpacing: '0.06em' }}>
                  rec
                </span>
              ) : null}
            </button>
          );
        })}
        {readOnly ? null : (
          <input
            className="ck-textarea"
            style={{ height: '28px', minHeight: 0, padding: '0 10px', flex: '1 1 170px', maxWidth: '340px', fontSize: 'var(--text-sm)', resize: 'none' }}
            placeholder="or say it your way"
            value={draft}
            disabled={saving}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitDraft}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitDraft();
              }
            }}
          />
        )}
        {saving ? (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>Saving…</span>
        ) : failed ? (
          <Badge tone="down" dot>
            Not saved
          </Badge>
        ) : answer ? (
          <Badge tone="up" dot>
            Answered · {answer}
          </Badge>
        ) : decision.recommendation ? (
          <Badge tone="accent">Recommended · {decision.recommendation}</Badge>
        ) : null}
      </div>
      <DecisionWhy decision={decision} />
    </div>
  );
}

/** Why the recommended option wins, under the options. Nothing when the spec gave no reason. */
export function DecisionWhy({ decision }: { decision: Decision }) {
  const why = decision.why?.trim();
  if (!decision.recommendation || !why) return null;
  return (
    <p style={{ margin: 0, paddingLeft: '30px', fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-normal)', color: 'var(--text-muted)' }}>
      <span style={{ color: 'var(--accent-text)' }}>Why {decision.recommendation}:</span> {why}
    </p>
  );
}
